import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — PARAMETRIC science actors: real subjects, auto-generated.
 *
 * Not hand-drawn one-offs: an Atom renders ANY element from its atomic number
 * (Bohr shells computed, electrons orbiting), and a Molecule renders from a
 * structure table or is auto-laid-out from its formula (H2O bent, CO2 linear,
 * CH4 tetrahedral, unknown formulas → radial auto-layout). Pure functions of
 * `t` — deterministic, headlessly testable, preview === export.
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

// ─── Elements ─────────────────────────────────────────────────────────────
export interface ElementInfo {
  z: number;
  symbol: string;
  name: string;
}

/** Common elements (enough for school/creator topics; extend freely). */
export const ELEMENTS: ElementInfo[] = [
  { z: 1, symbol: "H", name: "Hydrogen" }, { z: 2, symbol: "He", name: "Helium" },
  { z: 3, symbol: "Li", name: "Lithium" }, { z: 4, symbol: "Be", name: "Beryllium" },
  { z: 5, symbol: "B", name: "Boron" }, { z: 6, symbol: "C", name: "Carbon" },
  { z: 7, symbol: "N", name: "Nitrogen" }, { z: 8, symbol: "O", name: "Oxygen" },
  { z: 9, symbol: "F", name: "Fluorine" }, { z: 10, symbol: "Ne", name: "Neon" },
  { z: 11, symbol: "Na", name: "Sodium" }, { z: 12, symbol: "Mg", name: "Magnesium" },
  { z: 13, symbol: "Al", name: "Aluminium" }, { z: 14, symbol: "Si", name: "Silicon" },
  { z: 15, symbol: "P", name: "Phosphorus" }, { z: 16, symbol: "S", name: "Sulfur" },
  { z: 17, symbol: "Cl", name: "Chlorine" }, { z: 18, symbol: "Ar", name: "Argon" },
  { z: 19, symbol: "K", name: "Potassium" }, { z: 20, symbol: "Ca", name: "Calcium" },
  { z: 26, symbol: "Fe", name: "Iron" }, { z: 29, symbol: "Cu", name: "Copper" },
  { z: 30, symbol: "Zn", name: "Zinc" }, { z: 47, symbol: "Ag", name: "Silver" },
  { z: 79, symbol: "Au", name: "Gold" }, { z: 82, symbol: "Pb", name: "Lead" },
  { z: 92, symbol: "U", name: "Uranium" },
];

/**
 * Bohr-diagram shell occupancy for atomic number Z (illustrative K-L-M-N…
 * filling with capacities 2/8/8/18/32 — matches classroom Bohr diagrams for
 * Z ≤ 20; heavier elements render a sensible simplified diagram).
 */
export function bohrShells(z: number): number[] {
  const caps = [2, 8, 8, 18, 32];
  const shells: number[] = [];
  let left = Math.max(0, Math.floor(z));
  for (const cap of caps) {
    if (left <= 0) break;
    const take = Math.min(cap, left);
    shells.push(take);
    left -= take;
  }
  if (left > 0) shells.push(left); // beyond our caps — dump the rest visually
  return shells;
}

/** Find an element mentioned in text (symbol token or English name). */
export function findElement(text: string): ElementInfo | null {
  const s = ` ${text} `;
  const lower = s.toLowerCase();
  // Name match first (unambiguous), longest names first so "carbon" ≠ "boron" overlap issues.
  const byName = [...ELEMENTS].sort((a, b) => b.name.length - a.name.length)
    .find((e) => lower.includes(e.name.toLowerCase()));
  if (byName) return byName;
  // Symbol match: standalone token, case-sensitive-ish ("He" not "the").
  const tokens = s.split(/[^A-Za-z0-9]+/).filter(Boolean);
  for (const e of [...ELEMENTS].sort((a, b) => b.symbol.length - a.symbol.length)) {
    if (tokens.some((tok) => tok === e.symbol)) return e;
  }
  return null;
}

// ─── Molecules ────────────────────────────────────────────────────────────
export interface MolAtom { el: string; x: number; y: number }
export interface MolBond { a: number; b: number; order: 1 | 2 | 3 }
export interface Molecule {
  formula: string;
  name: string;
  atoms: MolAtom[];
  bonds: MolBond[];
}

/** CPK-ish colors, tuned to read on both dark and light themes. */
export const CPK: Record<string, string> = {
  H: "#e8e8f0", C: "#5a6472", O: "#e0533d", N: "#3b6fe0", S: "#d9b83a",
  P: "#e07b39", Cl: "#4fc94f", Na: "#ab5cf2", K: "#8f40d4", Ca: "#3dc9a0",
  Fe: "#b0705a", Mg: "#7ac96e", default: "#8a93a6",
};

const D = 1; // unit bond length in layout space (scaled at render)

/** Curated structures for the most-taught molecules. */
export const MOLECULES: Molecule[] = [
  { formula: "H2O", name: "Water", atoms: [{ el: "O", x: 0, y: 0 }, { el: "H", x: -0.79 * D, y: 0.61 * D }, { el: "H", x: 0.79 * D, y: 0.61 * D }], bonds: [{ a: 0, b: 1, order: 1 }, { a: 0, b: 2, order: 1 }] },
  { formula: "CO2", name: "Carbon dioxide", atoms: [{ el: "C", x: 0, y: 0 }, { el: "O", x: -D, y: 0 }, { el: "O", x: D, y: 0 }], bonds: [{ a: 0, b: 1, order: 2 }, { a: 0, b: 2, order: 2 }] },
  { formula: "O2", name: "Oxygen", atoms: [{ el: "O", x: -0.5 * D, y: 0 }, { el: "O", x: 0.5 * D, y: 0 }], bonds: [{ a: 0, b: 1, order: 2 }] },
  { formula: "N2", name: "Nitrogen", atoms: [{ el: "N", x: -0.5 * D, y: 0 }, { el: "N", x: 0.5 * D, y: 0 }], bonds: [{ a: 0, b: 1, order: 3 }] },
  { formula: "H2", name: "Hydrogen", atoms: [{ el: "H", x: -0.5 * D, y: 0 }, { el: "H", x: 0.5 * D, y: 0 }], bonds: [{ a: 0, b: 1, order: 1 }] },
  { formula: "CO", name: "Carbon monoxide", atoms: [{ el: "C", x: -0.5 * D, y: 0 }, { el: "O", x: 0.5 * D, y: 0 }], bonds: [{ a: 0, b: 1, order: 3 }] },
  { formula: "CH4", name: "Methane", atoms: [{ el: "C", x: 0, y: 0 }, { el: "H", x: 0, y: -D }, { el: "H", x: D, y: 0 }, { el: "H", x: 0, y: D }, { el: "H", x: -D, y: 0 }], bonds: [{ a: 0, b: 1, order: 1 }, { a: 0, b: 2, order: 1 }, { a: 0, b: 3, order: 1 }, { a: 0, b: 4, order: 1 }] },
  { formula: "NH3", name: "Ammonia", atoms: [{ el: "N", x: 0, y: -0.15 * D }, { el: "H", x: -0.87 * D, y: 0.55 * D }, { el: "H", x: 0, y: 0.75 * D }, { el: "H", x: 0.87 * D, y: 0.55 * D }], bonds: [{ a: 0, b: 1, order: 1 }, { a: 0, b: 2, order: 1 }, { a: 0, b: 3, order: 1 }] },
  { formula: "NaCl", name: "Salt", atoms: [{ el: "Na", x: -0.6 * D, y: 0 }, { el: "Cl", x: 0.6 * D, y: 0 }], bonds: [{ a: 0, b: 1, order: 1 }] },
  {
    formula: "C6H12O6", name: "Glucose",
    atoms: [
      ...Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * TAU - Math.PI / 2;
        return { el: i === 0 ? "O" : "C", x: Math.cos(a) * D, y: Math.sin(a) * D }; // pyranose-style ring (5C + O)
      }),
      { el: "C", x: Math.cos(-Math.PI / 2 + TAU / 6) * 2 * D, y: Math.sin(-Math.PI / 2 + TAU / 6) * 2 * D - 0.4 }, // CH2OH arm
    ],
    bonds: [
      ...Array.from({ length: 6 }, (_, i) => ({ a: i, b: (i + 1) % 6, order: 1 as const })),
      { a: 1, b: 6, order: 1 },
    ],
  },
];

const MOL_NAMES: Record<string, string> = {
  water: "H2O", "carbon dioxide": "CO2", oxygen: "O2", nitrogen: "N2",
  hydrogen: "H2", "carbon monoxide": "CO", methane: "CH4", ammonia: "NH3",
  salt: "NaCl", "sodium chloride": "NaCl", glucose: "C6H12O6", sugar: "C6H12O6",
};

/** Parse "H2O"/"C6H12O6" → element counts (simple formulas, no parentheses). */
export function parseFormula(formula: string): Array<{ el: string; n: number }> {
  const out: Array<{ el: string; n: number }> = [];
  const re = /([A-Z][a-z]?)(\d*)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(formula)) !== null) {
    if (!m[0]) break;
    const el = m[1];
    const n = m[2] ? parseInt(m[2], 10) : 1;
    const existing = out.find((e) => e.el === el);
    if (existing) existing.n += n;
    else out.push({ el, n });
  }
  return out;
}

/**
 * AUTO-LAYOUT for a formula we don't have curated: rarest element becomes the
 * center, the rest ring around it, bonded to the center. Deterministic — the
 * "replicates for any molecule form" fallback.
 */
export function autoMolecule(formula: string): Molecule | null {
  const parts = parseFormula(formula);
  const total = parts.reduce((s, p) => s + p.n, 0);
  if (parts.length === 0 || total === 0 || total > 12) return null;
  if (total === 1) {
    return { formula, name: formula, atoms: [{ el: parts[0].el, x: 0, y: 0 }], bonds: [] };
  }
  const center = [...parts].sort((a, b) => a.n - b.n)[0];
  const atoms: MolAtom[] = [{ el: center.el, x: 0, y: 0 }];
  const bonds: MolBond[] = [];
  const peripheral: string[] = [];
  for (const p of parts) {
    const n = p.el === center.el ? p.n - 1 : p.n;
    for (let i = 0; i < n; i++) peripheral.push(p.el);
  }
  peripheral.forEach((el, i) => {
    const a = (i / peripheral.length) * TAU - Math.PI / 2;
    atoms.push({ el, x: Math.cos(a) * D, y: Math.sin(a) * D });
    bonds.push({ a: 0, b: atoms.length - 1, order: 1 });
  });
  return { formula, name: formula, atoms, bonds };
}

/** Molecule via an explicit formula token (H2O, CO2, H2S…) — a strong signal. */
export function findMoleculeByFormula(text: string): Molecule | null {
  const tokens = text.split(/[^A-Za-z0-9]+/).filter((tk) => /^(?:[A-Z][a-z]?\d*){2,}$/.test(tk) && /\d/.test(tk));
  for (const tok of tokens) {
    const curated = MOLECULES.find((mo) => mo.formula.toLowerCase() === tok.toLowerCase());
    if (curated) return curated;
    const auto = autoMolecule(tok);
    if (auto && auto.atoms.length > 1) return auto;
  }
  // Formula-like without digits (NaCl, CO) — require an exact curated match to avoid noise.
  const bare = text.split(/[^A-Za-z0-9]+/).filter((tk) => /^(?:[A-Z][a-z]?){2,3}$/.test(tk));
  for (const tok of bare) {
    const curated = MOLECULES.find((mo) => mo.formula === tok);
    if (curated) return curated;
  }
  return null;
}

/** Molecule via a common name ("water", "glucose") — weaker signal; router gates it. */
export function findMoleculeByName(text: string): Molecule | null {
  const lower = ` ${text.toLowerCase()} `;
  for (const [name, formula] of Object.entries(MOL_NAMES).sort((a, b) => b[0].length - a[0].length)) {
    if (lower.includes(name)) return MOLECULES.find((mo) => mo.formula === formula) ?? null;
  }
  return null;
}

/** Find a molecule in text: curated name → formula → auto-layout. */
export function findMolecule(text: string): Molecule | null {
  return findMoleculeByName(text) ?? findMoleculeByFormula(text);
}

/** Teacher-style formation equation: H2O → "2H + O → H2O". */
export function formulaEquation(mol: Molecule): string {
  const parts = parseFormula(mol.formula);
  if (parts.length === 0) return mol.formula;
  const left = parts.map((p) => `${p.n > 1 ? p.n : ""}${p.el}`).join(" + ");
  return `${left} → ${mol.formula}`;
}

// ─── Renderers ────────────────────────────────────────────────────────────
interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

const ASSEMBLY_S = 1.3; // seconds of nucleon fly-in before the nucleus "forms"
const easeOut = (x: number) => 1 - Math.pow(1 - x, 3);

/**
 * Bohr atom that ASSEMBLES: protons (accent) and neutrons (grey) fly in and
 * cluster into a nucleus, THEN the shells appear and electrons spiral in to
 * orbit — the animation acts out "protons and neutrons form a nucleus,
 * electrons orbit it". Any element, deterministic.
 */
export const AtomVisual: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const el = findElement(items.join(" ")) ?? ELEMENTS[5]; // default Carbon
  const shells = bohrShells(el.z);
  const cx = box.cx, cy = box.cy;
  const maxR = Math.min(box.w, box.h) * 0.42;
  const r0 = maxR * 0.22; // nucleus
  const ring = (i: number) => r0 + ((i + 1) / shells.length) * (maxR - r0);

  // ── phase 1: nucleons converge into a packed cluster ────────────────────
  const nNucleons = Math.min(14, Math.max(2, el.z <= 8 ? el.z * 2 : 14));
  const golden = 2.39996; // packing angle
  const nucleons = Array.from({ length: nNucleons }, (_, i) => {
    const targetR = r0 * 0.52 * Math.sqrt((i + 0.5) / nNucleons);
    const ta = i * golden;
    const tx = cx + Math.cos(ta) * targetR;
    const ty = cy + Math.sin(ta) * targetR;
    const fromA = (i / nNucleons) * TAU + 0.7;
    const fx = cx + Math.cos(fromA) * maxR * 1.15;
    const fy = cy + Math.sin(fromA) * maxR * 1.15;
    const p = easeOut(clamp01((t - i * 0.06) / 0.9)); // staggered fly-in
    return {
      x: fx + (tx - fx) * p,
      y: fy + (ty - fy) * p,
      proton: i % 2 === 0,
      vis: clamp01(t / 0.15),
    };
  });
  const formed = clamp01((t - ASSEMBLY_S) / 0.5); // nucleus body + symbol fade in
  const shellDelay = ASSEMBLY_S * 0.75;

  return (
    <g>
      {/* nucleons flying in (fade as the solid nucleus takes over) */}
      <g opacity={1 - formed * 0.55}>
        {nucleons.map((nu, i) => (
          <circle key={i} cx={nu.x} cy={nu.y} r={r0 * 0.24} opacity={nu.vis}
            fill={nu.proton ? theme.palette.primary : theme.palette.muted}
            stroke={theme.dark ? "rgba(255,255,255,0.25)" : "rgba(15,22,32,0.25)"} strokeWidth={1} />
        ))}
      </g>
      {/* nucleus (forms once the cluster settles) */}
      <circle cx={cx} cy={cy} r={r0 * (1 + 0.03 * Math.sin(t * 2.2))} fill={theme.palette.primary} opacity={0.22 * formed} />
      <circle cx={cx} cy={cy} r={r0 * 0.72} fill={theme.palette.primary} opacity={0.85 * formed} />
      <g opacity={formed}>
        <text x={cx} y={cy - unit * 0.4} textAnchor="middle" dominantBaseline="central"
          fontFamily={theme.display.family} fontWeight={800} fontSize={r0 * 0.62}
          fill={theme.dark ? "#12151a" : "#ffffff"}>{el.symbol}</text>
        <text x={cx} y={cy + r0 * 0.42} textAnchor="middle" dominantBaseline="central"
          fontFamily={theme.body.family} fontWeight={700} fontSize={r0 * 0.2}
          fill={theme.dark ? "#12151a" : "#ffffff"} opacity={0.9}>{el.z}</text>
      </g>
      {/* shells + electrons (arrive after the nucleus exists) */}
      {shells.map((count, si) => {
        const r = ring(si);
        const appear = clamp01((t - shellDelay - 0.25 - si * 0.25) / 0.5);
        const dir = si % 2 === 0 ? 1 : -1;
        const speed = 0.5 - si * 0.07;
        return (
          <g key={si} opacity={appear}>
            <circle cx={cx} cy={cy} r={r} fill="none" stroke={theme.palette.muted}
              strokeWidth={1.5} strokeOpacity={0.45} strokeDasharray="2 6" />
            {Array.from({ length: count }, (_, ei) => {
              const a = (ei / count) * TAU + t * speed * dir + si * 0.7;
              // electrons spiral in from outside their ring as the shell appears
              const er = r + (1 - appear) * unit * 6;
              const ex = cx + Math.cos(a) * er;
              const ey = cy + Math.sin(a) * er;
              return (
                <g key={ei}>
                  <circle cx={ex} cy={ey} r={unit * 1.05} fill={theme.palette.highlight} opacity={0.25} />
                  <circle cx={ex} cy={ey} r={unit * 0.6} fill={theme.palette.highlight} />
                </g>
              );
            })}
          </g>
        );
      })}
      {/* caption */}
      <text x={cx} y={cy + maxR + unit * 3} textAnchor="middle"
        fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 2.4}
        fill={theme.palette.ink} opacity={0.85}>
        {el.name} · {shells.join("-")}
      </text>
    </g>
  );
};

/** Ball-and-stick molecule with CPK colors and a gentle vibration. */
export const MoleculeVisual: React.FC<VProps> = ({ items, box, theme, unit, t }) => {
  const mol = findMolecule(items.join(" ")) ?? MOLECULES[0]; // default water
  const spanX = Math.max(...mol.atoms.map((a) => Math.abs(a.x)), 1);
  const spanY = Math.max(...mol.atoms.map((a) => Math.abs(a.y)), 1);
  const scale = Math.min((box.w * 0.36) / spanX, (box.h * 0.3) / spanY);
  const cx = box.cx, cy = box.cy - unit * 1.5;
  const pos = mol.atoms.map((a, i) => ({
    x: cx + a.x * scale + Math.sin(t * 2 + i * 1.7) * unit * 0.35,
    y: cy + a.y * scale + Math.cos(t * 1.7 + i * 2.3) * unit * 0.35,
    el: a.el,
  }));
  const radius = (el: string) => (el === "H" ? unit * 2.1 : unit * 3.4);
  const color = (el: string) => CPK[el] ?? CPK.default;
  return (
    <g>
      {mol.bonds.map((b, i) => {
        const A = pos[b.a], B = pos[b.b];
        const dx = B.x - A.x, dy = B.y - A.y;
        const len = Math.hypot(dx, dy) || 1;
        const nx = -dy / len, ny = dx / len; // normal for multi-bond offset
        const offsets = b.order === 1 ? [0] : b.order === 2 ? [-unit * 0.55, unit * 0.55] : [-unit * 0.8, 0, unit * 0.8];
        const appear = clamp01((t - 0.2 - i * 0.12) / 0.4);
        return (
          <g key={i} opacity={appear}>
            {offsets.map((o, j) => (
              <line key={j} x1={A.x + nx * o} y1={A.y + ny * o} x2={B.x + nx * o} y2={B.y + ny * o}
                stroke={theme.palette.sub} strokeWidth={unit * 0.55} strokeLinecap="round" strokeOpacity={0.8} />
            ))}
          </g>
        );
      })}
      {pos.map((p, i) => {
        const appear = clamp01((t - 0.15 - i * 0.1) / 0.4);
        const r = radius(p.el);
        return (
          <g key={i} opacity={appear} transform={`translate(${p.x} ${p.y}) scale(${0.7 + 0.3 * appear}) translate(${-p.x} ${-p.y})`}>
            <circle cx={p.x} cy={p.y} r={r} fill={color(p.el)} stroke={theme.dark ? "rgba(255,255,255,0.35)" : "rgba(15,22,32,0.35)"} strokeWidth={1.5} />
            <text x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central"
              fontFamily={theme.body.family} fontWeight={800} fontSize={r * 0.9}
              fill={p.el === "H" ? "#3a4150" : "#ffffff"}>{p.el}</text>
          </g>
        );
      })}
      <text x={cx} y={box.y + box.h - unit * 1.5} textAnchor="middle"
        fontFamily={theme.body.family} fontWeight={700} fontSize={unit * 2.4}
        fill={theme.palette.ink} opacity={0.85}>
        {mol.name} · {mol.formula}
      </text>
    </g>
  );
};
